import React, { useContext, useEffect, useState } from 'react';

import StarBorderRoundedIcon from '@mui/icons-material/StarBorderRounded';
import StarRoundedIcon from '@mui/icons-material/StarRounded';
import {
  Avatar,
  Button,
  Card,
  Chip,
  Divider,
  Grid,
  Link,
  Typography
} from '@mui/material';
import {
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  query,
  where
} from 'firebase/firestore';
import { useTranslation } from 'react-i18next';

import {
  addFavoriteMedia,
  deleteFavorite,
  favoriteMediasRef,
  mediasRef
} from '../../api/firebase/firestore/media';
import { usersRef } from '../../api/firebase/firestore/user';
import noImage from '../../components/assets/noImage.svg';
import {
  black,
  blackText,
  blue,
  blueGrayText,
  cardBorder
} from '../../constants/Colors';
import { screenType } from '../../constants/enum';
import { AuthContext } from '../../contexts/auth';
import { throwError } from '../../modules/error';

export interface articleInfoType {
  title: string;
  labels: string[];
  created: string;
  updated: string;
  image?: string | null;
  publisherName: string;
  face?: string | null;
}

export interface mainItemType {
  outline: string;
  feature: string;
  menu: string;
  caseStudy: string;
  message: string;
}

export interface infoItemType {
  mediaName: string;
  genre: string;
  type: string;
  overview: string;
  funnel: string;
  scale: string;
  target: string;
  targetType: string;
}

export interface pubInfoItemType {
  companyName: string;
  url: string;
  industry: string;
}

interface Props {
  screen: number;
  articleInfo: articleInfoType;
  mainItem: mainItemType;
  infoItem: infoItemType;
  pubInfoItem: pubInfoItemType;
  mediaId?: string;
}

const styles = {
  avatar: {
    '@media screen and (max-width:480px)': {
      height: '40px',
      width: '40px'
    },
    height: '48px',
    mr: '8px',
    width: '48px'
  },
  buttonInner: {
    alignItems: 'center',
    columnGap: '8px',
    display: 'flex',
    flexDirection: 'row'
  },
  date: {
    fontSize: '14px'
  },
  desc: {
    flex: 1,
    img: {
      height: 'auto',
      maxWidth: '100%'
    },
    whiteSpace: 'pre-wrap'
  },
  divider: {
    backgroundColor: '#DBDBE0',
    height: '1px',
    width: '100%'
  },
  dividerBold: {
    backgroundColor: black,
    height: '3px',
    width: '72px'
  },
  infoItemText: {
    fontSize: '14px',
    whiteSpace: 'pre-wrap',
    width: '100%'
  },
  infoTitle: {
    fontSize: {
      sm: '18px',
      xs: '20px'
    }
  },
  itemTitle: {
    fontSize: {
      sm: '20px',
      xs: '22px'
    },
    fontWeight: '28px',
    mb: '16px'
  },
  label: {
    backgroundColor: 'rgba(10, 126, 191, 0.08)',
    border: '1px solid ##0A7EBF',
    borderRadius: '4px'
  },
  likeButton: {
    '&:hover': {
      backgroundColor: '#F2F2F2',
      boxShadow: 'none',
      color: black
    },
    backgroundColor: '#F2F2F2',
    borderRadius: '4px',
    height: '36px',
    margin: {
      sm: '0 0 0 auto',
      xs: '0 auto'
    },
    maxWidth: '259px',
    padding: '6px 32px'
  },
  likeLine: {
    height: '20px',
    width: '1px'
  },
  text: {
    fontSize: {
      sm: '14px',
      xs: '16px'
    }
  },
  title: {
    fontSize: {
      sm: '28px',
      xs: '20px'
    },
    fontWeight: '700',
    mb: '16px'
  },
  topCard: {
    boxShadow: 'none',
    margin: {
      sm: '24px 24px 0',
      xs: '16px 16px 0'
    }
  },
  topCardPub: {
    boxShadow: 'none',
    margin: {
      sm: '24px',
      xs: '16px'
    }
  },
  underPubInfoDivider: {
    '@media screen and (max-width:480px)': {
      display: 'flex'
    },
    display: 'none',
    mt: '56px'
  }
};

const MediaArticle: React.FC<Props> = (props) => {
  const { t } = useTranslation();
  const { currentUser } = useContext(AuthContext);
  const { screen, articleInfo, mainItem, infoItem, pubInfoItem, mediaId } =
    props;

  const items = [
    { desc: mainItem.outline, title: t('mediaArticle.outline') },
    { desc: mainItem.feature, title: t('mediaArticle.feature') },
    { desc: mainItem.menu, title: t('mediaArticle.menu') },
    { desc: mainItem.caseStudy, title: t('mediaArticle.caseStudy') },
    { desc: mainItem.message, title: t('mediaArticle.message') }
  ];

  const infoItems = [
    { desc: infoItem.mediaName, title: t('mediaArticle.mediaName') },
    { desc: infoItem.genre, title: t('mediaArticle.genre') },
    { desc: infoItem.type, title: t('mediaArticle.type') },
    { desc: infoItem.overview, title: t('mediaArticle.overview') },
    { desc: infoItem.funnel, title: t('mediaArticle.funnel') },
    { desc: infoItem.scale, title: t('mediaArticle.scale') },
    { desc: infoItem.target, title: t('mediaArticle.target') },
    { desc: infoItem.targetType, title: t('mediaArticle.targetType') }
  ];

  const pubInfoItems = [
    { desc: pubInfoItem.companyName, title: t('mediaArticle.companyName') },
    { desc: pubInfoItem.url, title: t('mediaArticle.url'), urlFlg: true },
    { desc: pubInfoItem.industry, title: t('mediaArticle.industry') }
  ];

  // お気に入り機能
  const [favorite, setFavorite] = useState<boolean>(false);
  const [mediaFavoriteId, setMediaFavoriteId] = useState<string>('');
  const [favoriteCount, setFavoriteCount] = useState<number>();
  const [companyId, setCompanyId] = useState<string>('');

  useEffect(() => {
    void (async () => {
      try {
        if (screen !== screenType.publisher) {
          if (currentUser && mediaId) {
            const userRef = doc(usersRef, currentUser.uid);
            const userSnap = await getDoc(userRef);
            setCompanyId(userSnap.data()?.companyId as string);

            const favoriteMedias = favoriteMediasRef(mediaId);
            const q = query(
              favoriteMedias,
              where('uid', '==', currentUser.uid)
            );
            const querySnapshot = await getDocs(q);
            querySnapshot.docs.forEach((document) => {
              setMediaFavoriteId(document.id);
            });
            onSnapshot(q, (snap) => {
              setFavorite(!snap.empty);
            });
            const mediaRef = doc(mediasRef, mediaId);
            const mediaSnap = await getDoc(mediaRef);
            const count = mediaSnap.data()?.favoriteCount as number;
            setFavoriteCount(count);

            // お気に入り数のリアルタイム更新処理
            const unsubscribe = onSnapshot(mediaRef, (snap) => {
              const realtimeCount = snap.data()?.favoriteCount as number;
              setFavoriteCount(realtimeCount ?? 0);
            });

            return () => unsubscribe();
          }
        }
      } catch (e) {
        throwError(e);
      }
    })();
  }, [mediaId, favorite]);

  const [isFavoriteSubmitting, setIsFavoriteSubmitting] =
    useState<boolean>(false);

  const mediaFavoriteHandle = async (mId: string): Promise<void> => {
    try {
      setIsFavoriteSubmitting(true);
      if (currentUser) {
        if (favorite) {
          await deleteFavorite(mId, mediaFavoriteId);
          setFavorite(false);
        } else {
          await addFavoriteMedia(currentUser.uid, mId, companyId);
          setFavorite(true);
        }
      }
      setIsFavoriteSubmitting(false);
    } catch (e) {
      setIsFavoriteSubmitting(false);
      throwError(e);
    }
  };

  return (
    <>
      <Card
        sx={[
          cardBorder,
          screen !== screenType.publisher ? styles.topCard : styles.topCardPub
        ]}
      >
        <Grid sx={{ padding: { sm: '40px', xs: '32px 16px' } }}>
          <Grid>
            <Typography component="p" sx={[blackText, styles.title]}>
              {articleInfo.title}
            </Typography>
            <Grid
              container
              direction="row"
              justifyContent="space-between"
              sx={{ mb: '16px', rowGap: '8px' }}
            >
              <Typography
                component="div"
                sx={{ columnGap: '8px', display: 'flex' }}
              >
                {articleInfo.labels.map((label: string, index: number) => (
                  <Chip
                    sx={styles.label}
                    label={label}
                    color="primary"
                    variant="outlined"
                    key={index}
                  />
                ))}
              </Typography>
              <Typography component="div">
                <Typography
                  component="span"
                  sx={[{ mr: '16px' }, styles.date, blueGrayText]}
                >
                  {t('mediaArticle.created')}：{articleInfo.created ?? ''}
                </Typography>
                <Typography component="span" sx={[styles.date, blueGrayText]}>
                  {t('mediaArticle.updated')}：{articleInfo.updated}
                </Typography>
              </Typography>
            </Grid>
            <img
              src={articleInfo.image ?? noImage}
              style={{
                height: 'auto',
                objectFit: 'contain',
                width: '100%'
              }}
              alt="mediaImage"
            />
            <Grid
              container
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{
                flexDirection: {
                  sm: 'row',
                  xs: 'column'
                },
                mt: '16px',
                rowGap: '24px'
              }}
            >
              <Grid sx={{ marginRight: { sm: '0', xs: 'auto' } }}>
                <Grid container direction="row" alignItems="center">
                  <Avatar
                    sx={styles.avatar}
                    key={articleInfo.face}
                    src={articleInfo.face ?? noImage}
                  />
                  <Typography component="span" sx={[styles.text, blackText]}>
                    {articleInfo.publisherName}
                  </Typography>
                </Grid>
              </Grid>
              {screen !== screenType.publisher && (
                <Button
                  sx={styles.likeButton}
                  onClick={() => mediaId && mediaFavoriteHandle(mediaId)}
                  disabled={isFavoriteSubmitting}
                >
                  <Typography component="div" sx={styles.buttonInner}>
                    {!favorite ? (
                      <StarBorderRoundedIcon
                        sx={{
                          color: blueGrayText,
                          cursor: 'pointer'
                        }}
                      />
                    ) : (
                      <StarRoundedIcon
                        sx={{
                          color: '#FCBA04',
                          cursor: 'pointer'
                        }}
                      />
                    )}
                    <Typography component="span" sx={[styles.date, blackText]}>
                      {t('mediaArticle.like')}
                    </Typography>
                    <Divider orientation="vertical" sx={styles.likeLine} />
                    <Typography component="span" sx={[styles.date, blackText]}>
                      {favoriteCount}
                    </Typography>
                  </Typography>
                </Button>
              )}
            </Grid>
          </Grid>
          <Grid
            container
            justifyContent="space-between"
            sx={{
              flexDirection: {
                md: 'row',
                xs: 'column-reverse'
              },
              mb: '56px',
              mt: '56px'
            }}
          >
            <Grid item xs={12} md={8}>
              {items.map((item, index) => (
                <Grid sx={{ mb: '56px' }} key={index}>
                  <Typography component="p" sx={[styles.itemTitle, blackText]}>
                    {item.title}
                  </Typography>
                  <Grid container direction="row" sx={{ mb: '24px' }}>
                    <Typography
                      component="div"
                      sx={styles.dividerBold}
                    ></Typography>
                    <Typography
                      component="div"
                      sx={styles.divider}
                    ></Typography>
                  </Grid>
                  <Typography
                    component="p"
                    sx={styles.desc}
                    dangerouslySetInnerHTML={{ __html: item.desc }}
                  />
                </Grid>
              ))}
            </Grid>
            <Grid item xs={0} md={1}></Grid>
            <Grid item xs={12} md={3} sx={{ mb: '56px' }}>
              <Grid>
                <Typography
                  component="p"
                  sx={[styles.infoTitle, blackText, { mb: '24px' }]}
                >
                  {t('mediaArticle.mediaInfo')}
                </Typography>
                <Grid>
                  {infoItems.map((info, index) => (
                    <Grid container key={index} sx={{ mb: '24px' }}>
                      <Typography
                        component="p"
                        sx={[
                          styles.infoItemText,
                          blueGrayText,
                          { mr: '17px', width: '99px' }
                        ]}
                      >
                        {info.title}
                      </Typography>
                      <Typography
                        component="p"
                        sx={[
                          styles.infoItemText,
                          blackText,
                          { flex: 1, width: '180px' }
                        ]}
                      >
                        {info.desc}
                      </Typography>
                    </Grid>
                  ))}
                </Grid>
              </Grid>
              <Divider sx={{ mb: '56px', mt: '56px' }} />
              <Grid>
                <Typography
                  component="p"
                  sx={[styles.infoTitle, blackText, { mb: '24px' }]}
                >
                  {t('mediaArticle.publisherInfo')}
                </Typography>
                <Grid>
                  {pubInfoItems.map((pubInfo, index) => (
                    <Grid container key={index} sx={{ mb: '24px' }}>
                      <Typography
                        component="p"
                        sx={[
                          styles.infoItemText,
                          blueGrayText,
                          { mr: '17px', width: '99px' }
                        ]}
                      >
                        {pubInfo.title}
                      </Typography>
                      {pubInfo.urlFlg === true ? (
                        <Link
                          href={pubInfo.desc}
                          sx={[
                            styles.infoItemText,
                            {
                              color: blue,
                              flex: 1,
                              textDecoration: 'none',
                              width: '180px'
                            }
                          ]}
                        >
                          {pubInfo.desc}
                        </Link>
                      ) : (
                        <Typography
                          component="p"
                          sx={[
                            styles.infoItemText,
                            blackText,
                            { flex: 1, width: '180px' }
                          ]}
                        >
                          {pubInfo.desc}
                        </Typography>
                      )}
                    </Grid>
                  ))}
                  <Divider sx={styles.underPubInfoDivider} />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Card>
    </>
  );
};

export default MediaArticle;
